/**
 * Accès à l'API d'administration.
 *
 * Le frontend ne décide jamais s'il est authentifié : il présente un token et
 * le serveur tranche. Les helpers ci-dessous existent pour qu'aucun appel
 * d'écriture ne puisse partir sans en-tête Authorization.
 */

const TOKEN_KEY = 'adminToken';
const USERNAME_KEY = 'adminUsername';

export const getApiUrl = (endpoint) => {
  const baseUrl = (process.env.REACT_APP_API_ORIGIN || 'https://mrtchoukdev.com').replace(/\/$/, '');
  return `${baseUrl}/api${endpoint}`;
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const getUsername = () => localStorage.getItem(USERNAME_KEY);

export const storeSession = (token, username) => {
  localStorage.setItem(TOKEN_KEY, token);
  if (username) {
    localStorage.setItem(USERNAME_KEY, username);
  }
};

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USERNAME_KEY);
  // Vestige de l'ancienne authentification côté client : on le purge pour que
  // plus aucun code ne puisse s'y fier.
  localStorage.removeItem('adminAuthenticated');
};

/**
 * Erreur levée quand le serveur refuse la session.
 * Permet aux appelants de distinguer « pas autorisé » d'une panne réseau.
 */
export class UnauthorizedError extends Error {
  constructor(message = 'Session expirée, veuillez vous reconnecter') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

/**
 * Appel authentifié à l'API.
 * Un 401 purge la session locale : garder un token mort ne sert à rien et
 * masque la vraie raison des erreurs suivantes.
 */
export const apiFetch = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = { ...(options.headers || {}) };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(getApiUrl(endpoint), { ...options, headers });

  if (response.status === 401) {
    clearSession();
    const data = await response.json().catch(() => ({}));
    throw new UnauthorizedError(data.message);
  }

  return response;
};

/**
 * Demande au serveur si la session est valide.
 * C'est la seule réponse qui fait autorité pour afficher l'interface d'admin.
 */
export const verifySession = async () => {
  if (!getToken()) {
    return false;
  }

  try {
    const response = await apiFetch('/me.php', { cache: 'no-store' });
    if (!response.ok) {
      return false;
    }
    const data = await response.json();
    return data.authenticated === true;
  } catch (error) {
    // UnauthorizedError a déjà purgé la session ; une panne réseau ne doit pas
    // être interprétée comme une session valide.
    return false;
  }
};

/**
 * Déconnexion : révoque la session côté serveur avant de purger le navigateur.
 */
export const logout = async () => {
  try {
    await apiFetch('/logout.php', { method: 'POST' });
  } catch (error) {
    // La révocation serveur a pu échouer (réseau) : on purge quand même le
    // navigateur, la session expirera d'elle-même côté serveur.
  } finally {
    clearSession();
  }
};
